import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { services } from '../data/mock';

const Header = ({ onGetQuote }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesCloseTimeoutRef = useRef(null);
  const location = useLocation();

  const clearServicesCloseTimeout = () => {
    if (servicesCloseTimeoutRef.current) {
      clearTimeout(servicesCloseTimeoutRef.current);
      servicesCloseTimeoutRef.current = null;
    }
  };

  const openServicesMenu = () => {
    clearServicesCloseTimeout();
    setIsServicesOpen(true);
  };

  const closeServicesMenuWithDelay = () => {
    clearServicesCloseTimeout();
    servicesCloseTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
      servicesCloseTimeoutRef.current = null;
    }, 150);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      clearServicesCloseTimeout();
    };
  }, []);

  useEffect(() => {
    clearServicesCloseTimeout();
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services", hasDropdown: true },
    { name: "Engineering", path: "/engineering" },
    { name: "Industrial", path: "/industrial" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "Contact", path: "/contact" }
  ];

  const serviceMenuItems = [
    {
      slug: 'fdm-3d-printing',
      menuTitle: 'FDM 3D Printing',
      menuDescription: 'High-strength functional mechanical components'
    },
    {
      slug: 'resin-3d-printing',
      menuTitle: 'Resin Printing',
      menuDescription: 'High-resolution precision surface detailing'
    },
    {
      slug: 'reverse-engineering',
      menuTitle: 'Reverse Engineering',
      menuDescription: 'CAD reconstruction & legacy part replication'
    },
    {
      slug: 'laser-cutting',
      menuTitle: 'Laser Cutting',
      menuDescription: 'Precision sheet cutting and panel fabrication'
    },
    {
      slug: 'rapid-prototyping',
      menuTitle: 'Rapid Prototyping',
      menuDescription: 'Fast iteration for product validation'
    }
  ];

  const serviceMenuColumns = [
    serviceMenuItems.slice(0, 3),
    serviceMenuItems.slice(3)
  ];

  const isActiveLink = (path) => {
    if (path === '/services') {
      return location.pathname === '/services' || location.pathname.startsWith('/services/');
    }
    return location.pathname === path;
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="header-logo">
          <span className="logo-text">Maverxx</span>
        </Link>

        <nav className="header-nav desktop-nav">
          {navLinks.map((link) => (
            <div 
              key={link.name}
              className={`nav-item-wrapper ${link.hasDropdown ? 'services-nav-wrapper' : ''}`}
              onMouseEnter={() => link.hasDropdown && openServicesMenu()}
              onMouseLeave={() => link.hasDropdown && closeServicesMenuWithDelay()}
            >
              <Link
                to={link.path}
                className={`nav-link ${isActiveLink(link.path) ? 'active' : ''}`}
                data-testid={`header-nav-${link.name.toLowerCase()}-link`}
              >
                {link.name}
                {link.hasDropdown && <ChevronDown className="nav-chevron" />}
              </Link>
              
              {link.hasDropdown && (
                <div
                  className={`mega-menu-premium ${isServicesOpen ? 'open' : 'closed'}`}
                  data-testid="services-mega-menu"
                  onMouseEnter={openServicesMenu}
                  onMouseLeave={closeServicesMenuWithDelay}
                >
                  <div className="mega-menu-premium-content">
                    <div className="mega-menu-premium-grid" data-testid="services-mega-menu-grid">
                      {serviceMenuColumns.map((columnItems, columnIndex) => (
                        <div
                          key={`services-column-${columnIndex}`}
                          className={`mega-menu-column ${columnIndex === 0 ? 'with-divider' : ''}`}
                          data-testid={`services-mega-menu-column-${columnIndex + 1}`}
                        >
                          {columnItems.map((serviceItem) => (
                            <Link
                              key={serviceItem.slug}
                              to={`/services/${serviceItem.slug}`}
                              className="mega-menu-premium-item"
                              data-testid={`services-mega-menu-item-${serviceItem.slug}`}
                            >
                              <div className="mega-menu-premium-title">{serviceItem.menuTitle}</div>
                              <div className="mega-menu-premium-desc">{serviceItem.menuDescription}</div>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                    <div className="mega-menu-premium-footer">
                      <Link to="/services" className="mega-menu-premium-view-all" data-testid="services-mega-menu-view-all-link">
                        View All Services →
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="header-cta desktop-cta">
          <Button onClick={onGetQuote} className="header-cta-btn">
            Get a Quote
          </Button>
        </div>

        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="mobile-menu">
          {navLinks.map((link) => (
            <div key={link.name}>
              {link.hasDropdown ? (
                <>
                  <button
                    className="mobile-nav-link mobile-nav-dropdown"
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  >
                    {link.name}
                    <ChevronDown className={`mobile-chevron ${isMobileServicesOpen ? 'open' : ''}`} />
                  </button>
                  {isMobileServicesOpen && (
                    <div className="mobile-submenu">
                      <Link to="/services" className="mobile-submenu-item">
                        All Services
                      </Link>
                      {services.map((service) => (
                        <Link
                          key={service.id}
                          to={`/services/${service.slug}`}
                          className="mobile-submenu-item"
                        >
                          {service.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={link.path}
                  className="mobile-nav-link"
                >
                  {link.name}
                </Link>
              )}
            </div>
          ))}
          <Button onClick={() => { onGetQuote(); setIsMobileMenuOpen(false); }} className="mobile-cta-btn">
            Get a Quote
          </Button>
        </div>
      )}
    </header>
  );
};

export default Header;
